import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, Package, AlertTriangle, Flag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

type ConfirmState = "loading" | "ready" | "confirming" | "success" | "already_confirmed" | "error";

const ConfirmDelivery = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const orderId = searchParams.get("orderId");
  const token = searchParams.get("token");

  const [state, setState] = useState<ConfirmState>("loading");
  const [errorMessage, setErrorMessage] = useState("");
  const [confirmedAt, setConfirmedAt] = useState<string | null>(null);
  const [showDisputeForm, setShowDisputeForm] = useState(false);
  const [disputeReason, setDisputeReason] = useState("");
  const [disputeDescription, setDisputeDescription] = useState("");
  const [submittingDispute, setSubmittingDispute] = useState(false);

  const shortOrderId = orderId ? `#${orderId.slice(0, 8).toUpperCase()}` : "";

  // Validate params on mount
  useEffect(() => {
    if (!orderId || !token) {
      setState("error");
      setErrorMessage("Invalid confirmation link. Please check your email or WhatsApp for the correct link.");
      return;
    }
    setState("ready");
  }, [orderId, token]);

  const handleConfirm = async () => {
    if (!orderId || !token) return;

    setState("confirming");

    try {
      const supabaseUrl = "https://fdcqcibrdsuvmdwagulk.supabase.co";
      const res = await fetch(`${supabaseUrl}/functions/v1/confirm-delivery`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ orderId, token }),
      });

      const data = await res.json();

      if (data.success && data.alreadyConfirmed) {
        setState("already_confirmed");
        return;
      }

      if (!res.ok || !data.success) {
        setState("error");
        setErrorMessage(data.message || "Failed to confirm delivery. Please try again.");
        return;
      }

      setConfirmedAt(data.confirmedAt);
      setState("success");
    } catch {
      setState("error");
      setErrorMessage("Network error. Please check your connection and try again.");
    }
  };

  return (
    <div className="min-h-[60vh] flex items-center justify-center px-4 py-12">
      <Card className="w-full max-w-md shadow-lg">
        {/* Loading */}
        {state === "loading" && (
          <>
            <CardHeader className="text-center">
              <Loader2 className="h-10 w-10 animate-spin text-muted-foreground mx-auto" />
              <CardTitle className="mt-4">Validating link…</CardTitle>
            </CardHeader>
          </>
        )}

        {/* Ready to confirm */}
        {state === "ready" && (
          <>
            <CardHeader className="text-center space-y-2">
              <Package className="h-12 w-12 text-primary mx-auto" />
              <CardTitle>Confirm Your Delivery</CardTitle>
              <CardDescription>
                Order {shortOrderId}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground text-center">
                By confirming, you acknowledge that you have received your order in good condition.
              </p>
              <Button onClick={handleConfirm} className="w-full" size="lg">
                <CheckCircle2 className="mr-2 h-5 w-5" />
                Yes, I received my order
              </Button>
              <p className="text-xs text-muted-foreground text-center">
                If you don't confirm within 24 hours of delivery, your order will be automatically confirmed.
              </p>
            </CardContent>
          </>
        )}

        {/* Confirming */}
        {state === "confirming" && (
          <>
            <CardHeader className="text-center">
              <Loader2 className="h-10 w-10 animate-spin text-primary mx-auto" />
              <CardTitle className="mt-4">Confirming delivery…</CardTitle>
              <CardDescription>Please wait while we process your confirmation.</CardDescription>
            </CardHeader>
          </>
        )}

        {/* Success */}
        {state === "success" && (
          <>
            <CardHeader className="text-center space-y-2">
              <CheckCircle2 className="h-14 w-14 text-primary mx-auto" />
              <CardTitle>Delivery Confirmed!</CardTitle>
              <CardDescription>
                Order {shortOrderId} has been marked as completed.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {confirmedAt && (
                <p className="text-sm text-muted-foreground text-center">
                  Confirmed on {new Date(confirmedAt).toLocaleDateString("en-NG", {
                    year: "numeric", month: "long", day: "numeric", hour: "2-digit", minute: "2-digit",
                  })}
                </p>
              )}
              <p className="text-sm text-center text-muted-foreground">
                Thank you for shopping with us!
              </p>
              
              {/* Dispute section */}
              {!showDisputeForm ? (
                <div className="border-t pt-4 space-y-2">
                  <p className="text-xs text-muted-foreground text-center">
                    Having an issue with your order? You can raise a dispute within 24 hours.
                  </p>
                  <Button 
                    onClick={() => setShowDisputeForm(true)} 
                    variant="outline" 
                    className="w-full text-destructive border-destructive/30 hover:bg-destructive/10"
                    size="sm"
                  >
                    <Flag className="mr-2 h-4 w-4" />
                    Raise a Dispute
                  </Button>
                </div>
              ) : (
                <div className="border-t pt-4 space-y-3">
                  <h4 className="font-semibold text-sm">Raise a Dispute</h4>
                  <Select value={disputeReason} onValueChange={setDisputeReason}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select reason" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="wrong_item">Wrong item received</SelectItem>
                      <SelectItem value="damaged">Item damaged</SelectItem>
                      <SelectItem value="incomplete">Incomplete order</SelectItem>
                      <SelectItem value="not_as_described">Not as described</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                  <Textarea
                    placeholder="Describe your issue..."
                    value={disputeDescription}
                    onChange={(e) => setDisputeDescription(e.target.value)}
                    rows={3}
                  />
                  <div className="flex gap-2">
                    <Button
                      onClick={async () => {
                        if (!disputeReason) {
                          toast.error("Please select a reason");
                          return;
                        }
                        setSubmittingDispute(true);
                        try {
                          // Get order details for seller_id
                          const { data: orderData } = await supabase
                            .from('orders')
                            .select('seller_id, buyer_id')
                            .eq('id', orderId)
                            .single();

                          const { error } = await supabase.from('disputes').insert({
                            order_id: orderId,
                            buyer_id: orderData?.buyer_id || (await supabase.auth.getUser()).data.user?.id,
                            seller_id: orderData?.seller_id,
                            reason: disputeReason,
                            description: disputeDescription || null,
                          });

                          if (error) {
                            if (error.message.includes('row-level security')) {
                              toast.error("Dispute window has expired (24 hours after confirmation)");
                            } else {
                              throw error;
                            }
                          } else {
                            // Set order status to disputed to block escrow release
                            await supabase
                              .from('orders')
                              .update({ status: 'disputed', updated_at: new Date().toISOString() })
                              .eq('id', orderId);

                            // Notify admin via email
                            try {
                              await supabase.functions.invoke('send-transactional-email', {
                                body: {
                                  type: 'admin-dispute-alert',
                                  data: {
                                    orderId: orderId?.slice(0, 8).toUpperCase(),
                                    reason: disputeReason.replace(/_/g, ' '),
                                    description: disputeDescription || 'No description provided',
                                  }
                                }
                              });
                            } catch (emailErr) {
                              console.error('Failed to send admin dispute email:', emailErr);
                            }

                            toast.success("Dispute submitted. Our team will review it shortly.");
                            setShowDisputeForm(false);
                          }
                        } catch (err) {
                          console.error('Dispute error:', err);
                          toast.error("Failed to submit dispute. Please contact support.");
                        } finally {
                          setSubmittingDispute(false);
                        }
                      }}
                      disabled={submittingDispute}
                      size="sm"
                      className="flex-1"
                      variant="destructive"
                    >
                      {submittingDispute ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                      Submit Dispute
                    </Button>
                    <Button
                      onClick={() => setShowDisputeForm(false)}
                      variant="outline"
                      size="sm"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}

              <Button onClick={() => navigate("/buyer/orders")} variant="outline" className="w-full">
                View My Orders
              </Button>
            </CardContent>
          </>
        )}

        {/* Already confirmed */}
        {state === "already_confirmed" && (
          <>
            <CardHeader className="text-center space-y-2">
              <AlertTriangle className="h-12 w-12 text-warning mx-auto" />
              <CardTitle>Already Confirmed</CardTitle>
              <CardDescription>
                Order {shortOrderId} delivery was already confirmed.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button onClick={() => navigate("/buyer/orders")} variant="outline" className="w-full">
                View My Orders
              </Button>
            </CardContent>
          </>
        )}

        {/* Error */}
        {state === "error" && (
          <>
            <CardHeader className="text-center space-y-2">
              <XCircle className="h-12 w-12 text-destructive mx-auto" />
              <CardTitle>Confirmation Failed</CardTitle>
              <CardDescription>{errorMessage}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {orderId && token && (
                <Button onClick={handleConfirm} className="w-full">
                  Try Again
                </Button>
              )}
              <Button onClick={() => navigate("/")} variant="outline" className="w-full">
                Go Home
              </Button>
            </CardContent>
          </>
        )}
      </Card>
    </div>
  );
};

export default ConfirmDelivery;
